import React, { MouseEventHandler, useContext } from 'react';
import { Fragment } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
    UserIcon,
    Bars3Icon as MenuIcon,
    XMarkIcon,
} from '@heroicons/react/24/solid';
import { ReactComponent as Logo } from 'svg/logo.svg';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import classNames from 'layout/classNames';
import { AuthContext } from 'react-oidc-context';
import globalvarsConfig from '../globalvars.config';

export default function HeaderMenu() {
    const location = useLocation();
    const auth = useContext(AuthContext);
    let navigationBar = [
        { name: 'DOOH Forecasting', to: '/' },
    ]
    if (globalvarsConfig.CampaignVisualisationActive === 'True') {
        navigationBar = [
                ...navigationBar,
            { name: 'Campaign Visualisation', to: '/campaign-visual?' },
        ]
    }
    const navigation = navigationBar.map((menuItem) => ({
        ...menuItem,
        current: location.pathname === menuItem.to,
    }));

    const onSignOut: MouseEventHandler = (event) => {
        event.preventDefault();
        if (auth) {
            auth.signoutRedirect({
                post_logout_redirect_uri: globalvarsConfig.SsoConfig!.LogoutRedirectUri,
            });
        }
    };

    return (
        <Disclosure as="nav" className="bg-gray-800">
            {({ open }) => (
                <>
                    <div className="mx-auto px-2 sm:px-6 lg:px-8">
                        <div className="relative flex items-center justify-between h-16">
                            <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                                {/* Mobile menu button*/}
                                <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                                    <span className="sr-only">
                                        Open main menu
                                    </span>
                                    {open ? (
                                        <XMarkIcon
                                            className="block h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        <MenuIcon
                                            className="block h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    )}
                                </Disclosure.Button>
                            </div>
                            <div className="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start">
                                <div className="flex-shrink-0 flex items-center">
                                    <Logo className="block h-8 w-auto text-gray-50" />
                                </div>
                                <div className="hidden sm:block sm:ml-6">
                                    <div className="flex space-x-4">
                                        {navigation.map(
                                            ({ name, to, current }) => (
                                                <Link
                                                    key={name}
                                                    to={to}
                                                    className={classNames(
                                                        current
                                                            ? 'bg-gray-900 text-white'
                                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                                        'px-3 py-2 rounded-md text-sm font-medium'
                                                    )}
                                                    aria-current={
                                                        current && 'page'
                                                    }
                                                >
                                                    {name}
                                                </Link>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                                <Menu as="div" className="ml-3 relative">
                                    <div>
                                        <Menu.Button className="bg-gray-800 flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white">
                                            <span className="sr-only">
                                                Open user menu
                                            </span>
                                            <UserIcon className="h-8 w-8 rounded-full text-gray-50" />
                                        </Menu.Button>
                                    </div>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[2000]">
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <a
                                                        onClick={onSignOut}
                                                        href="#"
                                                        className={classNames(
                                                            active &&
                                                                'bg-gray-100',
                                                            'block px-4 py-2 text-sm text-gray-700'
                                                        )}
                                                    >
                                                        Sign out
                                                    </a>
                                                )}
                                            </Menu.Item>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                            </div>
                        </div>
                    </div>

                    <Disclosure.Panel className="sm:hidden">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            {navigation.map(({ name, to, current }) => (
                                <Disclosure.Button
                                    key={name}
                                    as="a"
                                    href={to}
                                    className={classNames(
                                        current
                                            ? 'bg-gray-900 text-white'
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                        'block px-3 py-2 rounded-md text-base font-medium'
                                    )}
                                    aria-current={current && 'page'}
                                >
                                    {name}
                                </Disclosure.Button>
                            ))}
                        </div>
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
}
